type ExtraData = Record<string, unknown> | undefined;

const NATIVE_FETCH_FAILED_TO_FETCH = 'Native fetch error: Failed to fetch';

// Keep these separate from ignoreErrors so native-import failures can be handled
// before they are filtered from regular Sentry reporting.
export const dynamicImportErrorsRegexs: Array<RegExp> = [
  /Failed to load module. module/,
  /Failed to fetch dynamically imported module/,
  /Unable to preload CSS/,
  /Importing a module script failed./,
  // Firefox uses a different error message for dynamic import() failures
  /error loading dynamically imported module/i,
];

const ignoreErrorsByExtraData: Array<(extraData: ExtraData) => boolean> = [
  (extraData): boolean =>
    extraData?.errorMessage === NATIVE_FETCH_FAILED_TO_FETCH,
];

export default {
  blacklistUrls: [
    /draft-js\/lib\/.*/,
    /draft-js\/node_modules\/.*/,
    /.*moz-extension.*/,
    /.*safari-extension.*/,
    /.*chrome-extension.*/,
    /\/\/www\.google-analytics\.com.*/,
    // These appear to be just from browser plugins
    /<anonymous>/,
    /EventEmitter.<anonymous>.*/,

    // dev JS domains:
    /dev\.stripe\.me/,
    /localhost/,

    'file*',
    /\?\(blob:.*/,
    /\?\(undefined\)/,
  ],
  whitelistUrls: [],
  ignoreErrors: [
    // Lastpass chunders this error in IE 11.
    "ReferenceError: 'pass' is undefined",
    'Permission denied to access property "href" on cross-origin object',

    // js getting injected by the iOS version of Chrome GCRWeb
    '__gCrWeb',
    /Failed to execute 'open' on 'XMLHttpRequest': Refused to connect to.*/,
    /Failed to execute 'send' on 'XMLHttpRequest': Refused to connect to.*/,

    // React internals, don't seem to be actionable:
    'Should not already be working.',
    /Minified React error #327.*/, // Same error as above, wrapped

    /Loading chunk \d+ failed/,
    /Loading CSS chunk \d+ failed/,

    // A JS parsing bug in old Android Chrome (http://go/jira/DASH-9807)
    "Unexpected token 'else'",

    // These can appear out of localStorage overflow, or a JSON response too big for device's memory.
    // It may be beneficial to capture spikes in volume of such errors, but besides that it's not actionable.
    'Out of memory',
    'out of memory',

    // Caused by SeleniumIDE browser extension (http://go/jira/DASH-9809)
    "Identifier 'originalPrompt' has already been declared",

    // Seems related to Edge on iOS (https://stackoverflow.com/a/69576781)
    "Can't find variable: instantSearchSDKJSBridgeClearHighlight",

    // Has something to do with UC Browser: https://en.wikipedia.org/wiki/UC_Browser
    // We don't reference 'UCBrowserMessageCenter' anywhere in our JS code.
    "Can't find variable: UCBrowserMessageCenter",

    // UC Browser 18.x on Android injects UCShellJava into the page and calls
    // sdkEventFire internally. In version 18.9.4 on Android 11, sdkEventFire
    // is not a function, causing this error to bubble up via window.onerror.
    // We have no UCShellJava references in our code. See RUN_SMOR-2246.
    'TypeError: UCShellJava.sdkEventFire is not a function',
    /UCShellJava\.\w+ is not a function/,

    // Most of these happen on Firefox iOS 14 (released Nov 2018)
    // We don't have any references to 'webkit.messageHandlers' in our JS code.
    /undefined is not an object \(evaluating '(window\.)?webkit\.messageHandlers.*/,
    "Can't find variable: webkit",
    "undefined is not an object (evaluating 'document.getElementsByName*",
    "undefined is not an object (evaluating 'document.getElementsByTagName('video')[0].webkitExitFullScreen')",

    // All of these happen on Safari 12.0 and Mobile Safari 12.0 (released Sep 2018)
    // We don't have any references to 'ceCurrentVideo.currentTime' in our JS code.
    "undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",

    // I couldn't find if this is caused by some specific extension or not.
    // We don't have any references to 'inputItem' in our JS code.
    "Identifier 'inputItem' has already been declared",

    // generic browser extension error, they have a lot of various keys and values
    // that might be present so we match against the substring.
    // Example this should match against:
    // Non-Error promise rejection captured with keys: message, name, stack
    'Non-Error promise rejection captured',
    'Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target',

    // ResizeObserver errors seem safe to ignore:
    // https://stackoverflow.com/a/50387233
    'ResizeObserver loop limit exceeded', // chrome
    'ResizeObserver loop completed with undelivered notifications.', // firefox

    // This error comes from a weird recaptcha extension
    'Missing required parameters: sitekey',

    // matching on errors like:
    // "SecurityError: Failed to read a named property 'documentElement' from 'Window': Blocked a frame with origin "https://dashboard.stripe.com" from access"
    // If we can't access documentElement, something is really wrong in the browser
    /SecurityError: Failed to read a named property/,

    // These errors happen when people download the HTML for the Dashboard and then run it locally (why are they doing this?!)
    "SecurityError: Failed to execute 'replaceState' on 'History'",
    "'file:///C:/'",

    // This error is from running out of local storage space
    'QuotaExceededError',

    // Firefox specific error that is handled / not useful
    'InvalidStateError: An attempt was made to use an object that is not, or is no longer, usable',

    // ServiceWorker errors that are okay to ignore.
    'The operation is insecure.',
    'Failed to get ServiceWorkerRegistration objects: The document is in an invalid state.',

    // these happens sometimes when Mapbox is rendering
    'Failed to initialize WebGL.',
    'WebGLRenderingContext',

    // https://jira.corp.stripe.com/browse/RUN_DASHPLAT-2060
    'TypeError: cancelled',
    'TypeError: Abgebrochen',
    'TypeError: cancelado',
    'TypeError: 취소됨',
    'TypeError: annulé',
    'TypeError: annullato',
    'TypeError: anulat',
    'TypeError: geannuleerd',
    'TypeError: 網絡連線中斷。',
    'TypeError: avbruten',
    'TypeError: kumottu',
    'TypeError: отменено',
    'TypeError: zrušené',

    // Native fetch / network
    // 'Failed to fetch' unhandled rejections
    'TypeError: Failed to fetch',
    'TypeError: Load failed',
    'Native fetch error: Load failed',
    'Native fetch error: Failed to fetch',
    /^ErrorBoundary error 'Failed to fetch'$/,

    // 'cannot parse response' unhandled rejections
    'TypeError: cannot parse response',
    'TypeError: impossible d’analyser la réponse',
    'TypeError: impossibile analizzare la risposta',
    'TypeError: 応答を解析できません',
    'TypeError: Parsen der Antwort nicht möglich.',
    'TypeError: не удается произвести анализ ответа',

    // 'NetworkError when attempting to fetch resource.' unhandled rejections
    'TypeError: NetworkError when attempting to fetch resource.',
    'Native fetch error: NetworkError when attempting to fetch resource.',

    // Some unsupported browsers load properly, but throw flatMap errors.
    /.*\.flat(Map)? is not a function/,
    "Object doesn't support property or method 'flat",

    // Error code 499 is when the client disconnects from the server
    'Network error: Response not successful: Received status code 499',

    // Occurs in Safari when a TCP connection fails while the request was in progress
    'TypeError: The network connection was lost.',
    'TypeError: La conexión de red se perdió.',
    'TypeError: la connessione è stata persa.',
    'TypeError: La connexion réseau a été perdue.',
    'TypeError: Die Netzwerkverbindung wurde unterbrochen.',
    'TypeError: Połączenie sieciowe zostało przerwane.',

    // No internet seems inactionable.
    'TypeError: The internet connection appears to be offline.',
    'TypeError: The Internet connection appears to be offline.',

    // https://jira.corp.stripe.com/browse/RUN_DASHPLAT-2895
    'Function statements require a function name',

    // https://jira.corp.stripe.com/browse/RUN_DASHPLAT-2912
    'Possible side-effect in debug-evaluate',

    // Occurs in Firefox when the user is using one profile on multiple Firefox version
    /ServiceWorker script .*sw-stripe-2.js .* encountered an error during installation./,
    'TypeError: Script https://dashboard.stripe.com/sw-stripe-2.js load failed',
    // Chrome AbortError when ServiceWorker registration is aborted by browser/navigation policy.
    // Non-actionable browser-level noise; does not affect ServiceWorker runtime behavior.
    // https://jira.corp.stripe.com/browse/RUN_SMOR-2245
    /AbortError: Failed to register a ServiceWorker.*sw-stripe-2\.js.*Operation has been aborted/,

    // Remove after Stripelet error boundaries are updated in:
    // https://jira.corp.stripe.com/browse/WEBAPP-48
    // https://jira.corp.stripe.com/browse/WEBAPP-49
    "Cannot read properties of undefined (reading 'js')",

    // This error most commonly happens in Mobile Safari.
    // It has happened a few times a day across projects for years
    // and has not been linked to a real isssue.
    'NotFoundError: The object can not be found here.',
    /^ErrorBoundary error 'The object can not be found here\.'$/,

    // An ongoing request was intentionally canceled by the user.
    'AbortError: The user aborted a request.',

    // This likely just means that the component fetching data unmounted before the data was returned.
    // https://github.com/apollographql/apollo-client/issues/10520#issuecomment-1419638742
    'AbortError: signal is aborted without reason',
    // bug in Firefox <70
    // https://stackoverflow.com/questions/53039068/aborterror-the-operation-was-aborted-error-when-adjusting-html-5-video-cur
    /AbortError: The operation was aborted/,

    // Error that originates from a browser extension, and adds to ambient JS error rate.
    /EvalError: Refused to evaluate a string as JavaScript because 'unsafe-eval' is not an allowed source of script in the following Content Security Policy directive/,
    // Same CSP eval error, alternate Chromium phrasing (seen in Chrome 142+, Opera 127+)
    /EvalError: Evaluating a string as JavaScript violates the following Content Security Policy directive/,
    // WebAssembly CSP error: not actionable, caused by browser CSP restrictions blocking WebAssembly compilation
    // https://sentry.corp.stripe.com/organizations/stripe/issues/20164162/
    /Compiling or instantiating WebAssembly module violates the following Content Security Policy directive/,

    // Caused by Capital One Shopping browser extension
    "Identifier 'listenerName' has already been declared",

    // Caused by metamask extension
    // https://github.com/MetaMask/metamask-mobile/issues/6715
    'Non-Error promise rejection captured with keys: code, data, message',
    'Non-Error promise rejection captured with keys: code, message',
    'Non-Error promise rejection captured with keys: message, name, stack',
    // browser extension
    "Identifier '__loops_widget_baseUrl' has already been declared",
    // Playwright injected js related error
    "Cannot read properties of null (reading 'shadowRoot')",
    // evernote browser extension
    // see: https://stackoverflow.com/questions/50226631/error-there-is-no-clipping-info-for-given-tab
    'There is no clipping info for given tab',
    // This is an error that can be thrown from bad Chrome extensions
    'Could not establish connection. Receiving end does not exist.',
    "Cannot read properties of undefined (reading 'glance_invoke')",
    "Cannot read properties of undefined (reading 'glance_load')",
    // Firefox variants from Glance cobrowse. See RUN_TERMINAL_DEVICE_MGMT-6606 and RUN_TERMINAL_DEVICE_MGMT-6607.
    /can't access property "glance_(?:load|invoke)", .* is null/,
    "Cannot read properties of undefined (reading 'audience')",
    "Cannot read properties of undefined (reading 'forwardevent')",
    // Random unhandled promise rejection, either a random browser extension or an OpenTelemetry setTimeout:
    // https://github.com/open-telemetry/opentelemetry-js/blob/54b14fbbe33e0cf38115ee8c5c934a4e27786186/packages/opentelemetry-sdk-trace-base/src/export/BatchSpanProcessorBase.ts#L177
    /^"Timeout"$/,
    /^"Timeout (b)"$/,
    /^Timeout$/,
    'Timeout (u)',
    'Non-Error promise rejection captured with value: Timeout',
    'Non-Error promise rejection captured with value: Timeout (B)',
    'Non-Error promise rejection captured with value: Timeout (b)',

    // Caused by a bug with Sentry involving Microsoft Outlook scanning
    /Object Not Found Matching Id:[1-4], MethodName:update, ParamCount:4/,

    // https://blog.mozilla.org/addons/2012/09/12/what-does-cant-access-dead-object-mean/
    "can't access dead object",
    "TypeError: can't access dead object",
  ],
  ignoreErrorsByExtraData,
};
